import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ProgressComponent } from '../progress/progress.component';

@Component( {
    selector: 'riege-file-dropzone',
    standalone: true,
    imports: [CommonModule, MatButtonModule, MatInputModule, MatIconModule, MatTooltipModule, ProgressComponent],
    templateUrl: './file-dropzone.component.html',
    styleUrls: ['./file-dropzone.component.scss']
} )
export class FileDropzoneComponent {
    @Output() fileChanged = new EventEmitter<File[]>();
    @Input() allowedSize = 0;
    @Input() allowedTypes: string[] = [];
    @Input() directUpload = true;
    @Input() multiple = false;
    @Input() progress = 0;

    //fakeImage for styling
    // fakeImage = { name: 'image_stock_big.jpg', progress: 42, lastModified: 1687262932243, webkitRelativePath: '', size: 14679474, type: 'image/jpeg' };

    /** reference on file list */
    files: any[] = [];
    isHovered = false;

    validSize = signal( true );
    validType = signal( true );

    typeOne = $localize`:@@hint-one-file-type:Supported file type is`;
    typeFew = $localize`:@@hint-few-file-types:Supported file types are`;

    /**
     * on file drop handler
     * @param $event - event triggered
     */
    onFileDropped( $event: any ) {
        this.unsetErrors();
        this.files = Array.from( $event );
        this.prepareFilesList();
    }

    /**
     * handle file from browsing
     * @param $event - event triggered
     */
    fileBrowseHandler( $event: any ) {
        this.unsetErrors();
        this.files = Array.from( $event.target.files );
        this.prepareFilesList();
    }

    /**
     * Delete file from files list
     * @param index (File index)
     */
    deleteFile( index: number ) {
        this.unsetErrors();
        this.files.splice( index, 1 );
    }

    // unset errors
    unsetErrors() {
        this.validType.set( true );
        this.validSize.set( true );
    }

    /**
     * Check type & size of files
     * when supported create array for
     * or trigger direct upload
     * @param files (Files List)
     */
    prepareFilesList() {
        for ( const file of this.files ) {
            let type: string = file.type.split( '/' )[1];

            // handle SVG mimetype ('svg+xml')
            const i = type.indexOf( '+' );
            if ( i > 0 ) {
                type = type.substring( 0, i );
            }

            // file type not supported
            if ( !this.allowedTypes.includes( type ) ) {
                this.validType.set( false );
                return;
            }

            // file size too supported
            if ( this.allowedSize < file.size ) {
                this.validSize.set( false );
                return;
            }

            if ( this.multiple ) {
                this.files.push( file );
            } else {
                this.files.splice( 0, 1, file );
                // trigger direct upload when configured
                if ( this.directUpload ) {
                    this.uploadFiles();
                }
            }
        }
    }

    /**
     * trigger upload event in parent component
     */
    uploadFiles() {
        this.fileChanged.emit( this.files );
    }

    /**
     * helper function to trigger hover effect on drag events
     */
    onDragEnter() {
        this.isHovered = !this.isHovered;
    }
    onDragEnd() {
        this.isHovered = !this.isHovered;
    }

    /**
     * format bytes
     * @param bytes (File size in bytes)
     * @param decimals (Decimals point)
     * @returns {string} formatted string of bytes
     */
    formatBytes( bytes: number, decimals: number = 0 ): string {
        if ( bytes === 0 ) {
            return '0 Bytes';
        }
        const k = 1024;
        const dm = decimals <= 0 ? 0 : decimals || 2;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor( Math.log( bytes ) / Math.log( k ) );
        return parseFloat( ( bytes / Math.pow( k, i ) ).toFixed( dm ) ) + ' ' + sizes[i];
    }
}
